import React, { useState, useEffect } from "react";
import { DatePicker, Spin, Form, Button, Space, Select, Modal, Tooltip, Typography } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import moment from "moment";
import useFetch from "use-http";
import { successN, errorN } from "../../general";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

const DAY_WIDTH = 110;
const NAME_WIDTH = 160;
const ROW_HEIGHT = 34;
const LOOKBACK_DAYS = 31;

const COLORS = {
    Occupied: "#52c41a",
    Blocked: "#1890ff",
    Closed: "#bfbfbf",
    Maintenance: "#fa8c16",
};

// Same as the Windows Planner tab (frmHome loadOccupancyCalendar): one row per unit, one bar per allocation.
// Dragging a bar to another unit row moves the guest, using the same call as Change Unit.
function Planner() {
    const { get, post, response, loading } = useFetch();
    const [form] = Form.useForm();

    const [rooms, setRooms] = useState([]);
    const [allRooms, setAllRooms] = useState([]);
    const [allocations, setAllocations] = useState([]);
    const [unitTypes, setUnitTypes] = useState([]);
    const [rangeStart, setRangeStart] = useState(null);
    const [dayCount, setDayCount] = useState(0);
    const [dragging, setDragging] = useState(null);
    const [overRoom, setOverRoom] = useState(null);

    const unitName = localStorage.getItem("unitcustomname") || "Unit";

    useEffect(() => {
        form.setFieldsValue({
            dtDateRange: [moment().startOf("day"), moment().startOf("day").add(6, "days")],
            unitType: "All",
        });
        loadData();
    }, []);

    async function loadData() {
        try {
            const values = await form.validateFields();
            const start = moment(values.dtDateRange[0]).startOf("day");
            const end = moment(values.dtDateRange[1]).startOf("day");

            if (end.diff(start, "days") > 62) {
                errorN("Please choose a date range of 62 days or less.");
                return;
            }

            const property = JSON.parse(localStorage.getItem("currentproperty"));

            const roomList = await get(`/api/hotels/${property.id}/rooms`);
            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem("session");
                    window.location.reload();
                }
                errorN(`Get ${unitName} - ${response.status}: ${response.statusText}. Please contact the administrator.`);
                return;
            }

            // The API only returns stays that start inside start..end, so look back to catch stays already running.
            const s = start.clone().subtract(LOOKBACK_DAYS, "days").utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
            const e = end.clone().add(1, "days").utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
            const allocationList = await get(`/api/hotels/${property.id}/allocations?start=${s}&end=${e}`);
            if (!response.ok) {
                errorN(`Get Allocations - ${response.status}: ${response.statusText}. Please contact the administrator.`);
                return;
            }

            const sorted = (roomList || []).slice().sort((a, b) => (a.data.no > b.data.no ? 1 : -1));

            const types = [];
            for (const r of sorted) {
                const id = itemOf(r);
                if (id !== null && !types.find(t => t.id === id)) {
                    types.push({ id, name: r.data.service ? r.data.service.name : String(id) });
                }
            }

            let shown = sorted;
            if (values.unitType !== "All") {
                shown = sorted.filter(r => itemOf(r) === values.unitType);
            }

            setUnitTypes(types);
            setAllRooms(sorted);
            setRooms(shown);
            setAllocations(allocationList || []);
            setRangeStart(start);
            setDayCount(end.diff(start, "days") + 1);
        } catch (err) {
            errorN(`Error Load Planner: ${err}`);
        }
    }

    function itemOf(room) {
        if (room.item !== undefined && room.item !== null) return room.item;
        if (room.data && room.data.service) return room.data.service.id;
        return null;
    }

    function startOf(a) {
        return moment(a.sdate || a.start);
    }

    // Windows shows a closed stay up to its actual check out.
    function endOf(a) {
        if (a.status === "Closed" && a.checkout) return moment(a.checkout);
        return moment(a.edate || a.end);
    }

    function isMaintenance(a) {
        return !(a.data && a.data.guest);
    }

    function labelOf(a) {
        if (a.data && a.data.guest) return a.data.guest.name;
        const cat = a.data && a.data.category ? a.data.category.toUpperCase() : "BLOCK";
        const reason = a.data && a.data.reason ? a.data.reason : "";
        return `${cat} : ${reason}`;
    }

    function colorOf(a) {
        if (isMaintenance(a)) return COLORS.Maintenance;
        return COLORS[a.status] || "#722ed1";
    }

    // Same rules as Windows schedulerControl1_AppointmentDrop.
    function dragBlockReason(a) {
        const rangeEnd = rangeStart.clone().add(dayCount, "days");
        if (endOf(a).isAfter(rangeEnd)) return "This stay ends after the dates shown. Widen the date range first.";
        if (endOf(a).isBefore(moment())) return "This stay has already ended.";
        if (a.status === "Closed") return "This stay is closed.";
        if (isMaintenance(a)) return "Maintenance blocks cannot be moved here.";
        return null;
    }

    function dropBlockReason(a, roomId) {
        const s = startOf(a);
        const e = endOf(a);
        for (const x of allocations) {
            if (x.id === a.id || x.room !== roomId) continue;
            const xs = startOf(x);
            const xe = endOf(x);
            if (xs.isSameOrBefore(e) && xe.isSameOrAfter(s)) {
                return `The ${unitName.toLowerCase()} already has an allocation for these dates (${labelOf(x)}).`;
            }
        }
        return null;
    }

    function roomNo(roomId) {
        const r = allRooms.find(x => x.id === roomId);
        return r ? r.data.no : "";
    }

    // Same checks as the Change Unit dialog: the server's room availability for the stay's dates,
    // and a vacant room when the guest has already arrived.
    async function availabilityBlockReason(a, roomId) {
        const property = JSON.parse(localStorage.getItem("currentproperty"));
        const list = await get(`/api/hotels/${property.id}/availability_rooms?start=${a.start}&end=${a.end}`);
        if (!response.ok) {
            return `Could not check availability (${response.status}: ${response.statusText}).`;
        }
        const free = (list || []).some(i => (i.rooms || []).some(x => x.id === roomId));
        if (!free) {
            return `${unitName} ${roomNo(roomId)} is not available for these dates.`;
        }
        const target = allRooms.find(x => x.id === roomId);
        if (startOf(a).isSameOrBefore(moment()) && target && target.occupancy !== "vacant") {
            return `${unitName} ${roomNo(roomId)} is not vacant now.`;
        }
        return null;
    }

    async function onDrop(roomId) {
        const a = dragging;
        setDragging(null);
        setOverRoom(null);
        if (!a || a.room === roomId) return;

        let reason = dropBlockReason(a, roomId);
        if (reason === null) {
            reason = await availabilityBlockReason(a, roomId);
        }
        if (reason !== null) {
            errorN(`Invalid: ${reason}`);
            return;
        }

        Modal.confirm({
            title: `Move ${labelOf(a)}?`,
            content: `From ${unitName} ${roomNo(a.room)} to ${unitName} ${roomNo(roomId)}, ${startOf(a).format("DD-MM-YYYY")} to ${endOf(a).format("DD-MM-YYYY")}. If an access card was issued, write a new card after the move.`,
            okText: "Move",
            cancelText: "Cancel",
            onOk: () => changeUnit(a, roomId),
        });
    }

    async function changeUnit(a, roomId) {
        try {
            await post(`/api/allocations/${a.id}/change`, { room: roomId });
            if (response.ok) {
                successN(`${unitName} changed.`);
            } else {
                errorN(`Change ${unitName} - ${response.status}: ${response.statusText}. Please contact the administrator.`);
            }
        } catch (err) {
            errorN(`Error Change ${unitName}: ${err}. Please contact the administrator.`);
        }
        loadData();
    }

    function barsFor(roomId) {
        if (!rangeStart) return [];
        const rangeEnd = rangeStart.clone().add(dayCount, "days");
        return allocations
            .filter(a => a.room === roomId)
            .filter(a => startOf(a).isBefore(rangeEnd) && endOf(a).isAfter(rangeStart))
            .map(a => {
                const s = moment.max(startOf(a), rangeStart);
                const e = moment.min(endOf(a), rangeEnd);
                const left = (s.diff(rangeStart, "minutes") / 1440) * DAY_WIDTH;
                const width = Math.max((e.diff(s, "minutes") / 1440) * DAY_WIDTH, 8);
                return { a, left, width };
            });
    }

    const days = [];
    for (let i = 0; i < dayCount; i++) {
        days.push(rangeStart.clone().add(i, "days"));
    }
    const gridWidth = NAME_WIDTH + dayCount * DAY_WIDTH;

    return (
        <div style={{ padding: 20, backgroundColor: "white" }}>
            <Form form={form} layout="inline">
                <Space wrap>
                    <Form.Item label="Dates" name="dtDateRange" rules={[{ required: true, message: "Dates missing!" }]}>
                        <RangePicker format="DD-MM-YYYY" />
                    </Form.Item>
                    <Form.Item label={`${unitName} Type`} name="unitType">
                        <Select style={{ width: 200 }}>
                            <Option value="All">All</Option>
                            {unitTypes.map(t => (
                                <Option key={t.id} value={t.id}>{t.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Button type="primary" icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
                        Load
                    </Button>
                </Space>
            </Form>

            <div style={{ margin: "10px 0" }}>
                <Space wrap>
                    {Object.keys(COLORS).map(k => (
                        <span key={k}>
                            <span style={{ display: "inline-block", width: 12, height: 12, background: COLORS[k], marginRight: 4 }} />
                            <Text>{k === "Blocked" ? "Reserved" : k}</Text>
                        </span>
                    ))}
                    <Text type="secondary">Drag a bar to another {unitName.toLowerCase()} to move the guest.</Text>
                </Space>
            </div>

            <Spin spinning={loading}>
                <div style={{ overflow: "auto", maxHeight: "65vh", border: "1px solid #f0f0f0" }}>
                    <div style={{ width: gridWidth, position: "relative" }}>
                        <div style={{ display: "flex", position: "sticky", top: 0, zIndex: 3, background: "#fafafa", borderBottom: "1px solid #f0f0f0" }}>
                            <div style={{ width: NAME_WIDTH, flex: "none", padding: "6px 8px", fontWeight: 600, position: "sticky", left: 0, background: "#fafafa", zIndex: 4 }}>
                                {unitName} No.
                            </div>
                            {days.map(d => (
                                <div
                                    key={d.format("YYYYMMDD")}
                                    style={{
                                        width: DAY_WIDTH,
                                        flex: "none",
                                        padding: "6px 4px",
                                        textAlign: "center",
                                        borderLeft: "1px solid #f0f0f0",
                                        fontWeight: d.isSame(moment(), "day") ? 700 : 400,
                                        color: d.day() === 0 || d.day() === 6 ? "#cf1322" : undefined,
                                    }}
                                >
                                    {d.format("ddd DD-MM")}
                                </div>
                            ))}
                        </div>

                        {rooms.map(r => (
                            <div
                                key={r.id}
                                style={{
                                    display: "flex",
                                    height: ROW_HEIGHT,
                                    borderBottom: "1px solid #f0f0f0",
                                    background: overRoom === r.id ? "#e6f7ff" : undefined,
                                }}
                                onDragOver={e => {
                                    if (dragging) {
                                        e.preventDefault();
                                        if (overRoom !== r.id) setOverRoom(r.id);
                                    }
                                }}
                                onDrop={e => {
                                    e.preventDefault();
                                    onDrop(r.id);
                                }}
                            >
                                <div
                                    style={{
                                        width: NAME_WIDTH,
                                        flex: "none",
                                        padding: "6px 8px",
                                        position: "sticky",
                                        left: 0,
                                        background: "white",
                                        zIndex: 2,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}
                                >
                                    <b>{r.data.no}</b> {r.data.service ? r.data.service.name : ""}
                                </div>
                                <div style={{ position: "relative", width: dayCount * DAY_WIDTH, flex: "none" }}>
                                    {days.map((d, i) => (
                                        <div
                                            key={i}
                                            style={{ position: "absolute", left: i * DAY_WIDTH, top: 0, bottom: 0, borderLeft: "1px solid #f5f5f5" }}
                                        />
                                    ))}
                                    {barsFor(r.id).map(({ a, left, width }) => {
                                        const blocked = dragBlockReason(a);
                                        return (
                                            <Tooltip
                                                key={a.id}
                                                title={
                                                    <div>
                                                        <div>{labelOf(a)}</div>
                                                        <div>{startOf(a).format("DD-MM-YYYY hh:mm A")} to {endOf(a).format("DD-MM-YYYY hh:mm A")}</div>
                                                        <div>Status: {isMaintenance(a) ? "Maintenance" : a.status}</div>
                                                        {blocked ? <div>Can't move: {blocked}</div> : null}
                                                    </div>
                                                }
                                            >
                                                <div
                                                    draggable={blocked === null}
                                                    onDragStart={e => {
                                                        e.dataTransfer.setData("text/plain", String(a.id));
                                                        setDragging(a);
                                                    }}
                                                    onDragEnd={() => {
                                                        setDragging(null);
                                                        setOverRoom(null);
                                                    }}
                                                    style={{
                                                        position: "absolute",
                                                        left,
                                                        width,
                                                        top: 4,
                                                        height: ROW_HEIGHT - 8,
                                                        background: colorOf(a),
                                                        color: "white",
                                                        borderRadius: 4,
                                                        padding: "2px 6px",
                                                        fontSize: 12,
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        cursor: blocked === null ? "grab" : "default",
                                                        opacity: dragging && dragging.id === a.id ? 0.5 : 1,
                                                        zIndex: 1,
                                                    }}
                                                >
                                                    {labelOf(a)}
                                                </div>
                                            </Tooltip>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </Spin>
        </div>
    );
}

export default Planner;
